import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { DollarSign, Shield, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  CoverageAmountType,
  EarningsBase,
  RoundingRule,
} from "@shared/schema";

interface SupplementalConfigFormProps {
  planId: number;
  planType: "accident" | "critical_illness";
  onSave?: () => void;
}

export function SupplementalConfigForm({ planId, planType, onSave }: SupplementalConfigFormProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    benefitScheduleType: "fixed" as "fixed" | "tiered",
    coverageAmountType: CoverageAmountType.FLAT_AMOUNT,
    flatCoverageAmount: "",
    earningsMultiple: "",
    earningsBase: "" as string,
    minimumCoverage: "",
    maximumCoverage: "",
    coverageIncrement: "",
    guaranteedIssueAmount: "",
    portabilityAllowed: false,
    conversionAllowed: false,
    ratePerUnit: "",
    rateUnitBasis: "1000",
    roundingRule: RoundingRule.NONE,
  });

  // Load existing config
  useEffect(() => {
    fetch(`/api/plans/${planId}/supplemental-config`, { credentials: "include" })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setFormData({
            benefitScheduleType: data.benefitScheduleType || "fixed",
            coverageAmountType: data.coverageAmountType || CoverageAmountType.FLAT_AMOUNT,
            flatCoverageAmount: data.flatCoverageAmount || "",
            earningsMultiple: data.earningsMultiple || "",
            earningsBase: data.earningsBase || "",
            minimumCoverage: data.minimumCoverage || "",
            maximumCoverage: data.maximumCoverage || "",
            coverageIncrement: data.coverageIncrement || "",
            guaranteedIssueAmount: data.guaranteedIssueAmount || "",
            portabilityAllowed: data.portabilityAllowed || false,
            conversionAllowed: data.conversionAllowed || false,
            ratePerUnit: data.ratePerUnit || "",
            rateUnitBasis: data.rateUnitBasis || "1000",
            roundingRule: data.roundingRule || RoundingRule.NONE,
          });
        }
      })
      .catch(() => {});
  }, [planId]);

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/plans/${planId}/supplemental-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ planId, ...formData }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to save");
      }

      toast({ title: "Saved", description: "Supplemental plan configuration updated." });
      onSave?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isEarningsBased = formData.coverageAmountType === CoverageAmountType.EARNINGS_MULTIPLE ||
    formData.coverageAmountType === CoverageAmountType.EARNINGS_PERCENTAGE;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Benefit Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {planType === "accident" ? "Accident" : "Critical Illness"} Benefit Schedule
          </CardTitle>
          <CardDescription>
            Configure how benefits are structured for this plan
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Benefit Schedule Type</Label>
            <Select value={formData.benefitScheduleType} onValueChange={(v) => updateField("benefitScheduleType", v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed Benefits</SelectItem>
                <SelectItem value="tiered">Tiered Benefits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Coverage Amount */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Coverage Amount Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Coverage Amount Type</Label>
            <Select
              value={formData.coverageAmountType}
              onValueChange={(v) => updateField("coverageAmountType", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CoverageAmountType.FLAT_AMOUNT}>Flat Amount</SelectItem>
                <SelectItem value={CoverageAmountType.EARNINGS_MULTIPLE}>Multiple of Earnings</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.coverageAmountType === CoverageAmountType.FLAT_AMOUNT && (
            <div>
              <Label htmlFor="flatCoverageAmount">Flat Coverage Amount ($)</Label>
              <Input
                id="flatCoverageAmount"
                type="number"
                value={formData.flatCoverageAmount}
                onChange={(e) => updateField("flatCoverageAmount", e.target.value)}
                placeholder="10000"
              />
            </div>
          )}

          {isEarningsBased && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="earningsMultiple">Earnings Multiple</Label>
                <Input
                  id="earningsMultiple"
                  type="number"
                  step="0.5"
                  value={formData.earningsMultiple}
                  onChange={(e) => updateField("earningsMultiple", e.target.value)}
                  placeholder="1"
                />
              </div>
              <div>
                <Label>Earnings Base</Label>
                <Select value={formData.earningsBase} onValueChange={(v) => updateField("earningsBase", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EarningsBase.BASE_SALARY}>Base Salary</SelectItem>
                    <SelectItem value={EarningsBase.TOTAL_COMPENSATION}>Total Compensation</SelectItem>
                    <SelectItem value={EarningsBase.W2_EARNINGS}>W2 Earnings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <Separator />

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="minimumCoverage">Minimum ($)</Label>
              <Input
                id="minimumCoverage"
                type="number"
                value={formData.minimumCoverage}
                onChange={(e) => updateField("minimumCoverage", e.target.value)}
                placeholder="5000"
              />
            </div>
            <div>
              <Label htmlFor="maximumCoverage">Maximum ($)</Label>
              <Input
                id="maximumCoverage"
                type="number"
                value={formData.maximumCoverage}
                onChange={(e) => updateField("maximumCoverage", e.target.value)}
                placeholder="100000"
              />
            </div>
            <div>
              <Label htmlFor="coverageIncrement">Increment ($)</Label>
              <Input
                id="coverageIncrement"
                type="number"
                value={formData.coverageIncrement}
                onChange={(e) => updateField("coverageIncrement", e.target.value)}
                placeholder="5000"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="guaranteedIssueAmount">Guaranteed Issue Amount ($)</Label>
            <Input
              id="guaranteedIssueAmount"
              type="number"
              value={formData.guaranteedIssueAmount}
              onChange={(e) => updateField("guaranteedIssueAmount", e.target.value)}
              placeholder="50000"
            />
          </div>
        </CardContent>
      </Card>

      {/* Rate & Additional Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Rate & Additional Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ratePerUnit">Rate per Unit ($)</Label>
              <Input
                id="ratePerUnit"
                type="number"
                step="0.0001"
                value={formData.ratePerUnit}
                onChange={(e) => updateField("ratePerUnit", e.target.value)}
                placeholder="0.15"
              />
            </div>
            <div>
              <Label htmlFor="rateUnitBasis">Unit Basis ($)</Label>
              <Input
                id="rateUnitBasis"
                type="number"
                value={formData.rateUnitBasis}
                onChange={(e) => updateField("rateUnitBasis", e.target.value)}
                placeholder="1000"
              />
            </div>
          </div>

          <div>
            <Label>Rounding Rule</Label>
            <Select value={formData.roundingRule} onValueChange={(v) => updateField("roundingRule", v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={RoundingRule.NONE}>No Rounding</SelectItem>
                <SelectItem value={RoundingRule.NEAREST_THOUSAND}>Nearest $1,000</SelectItem>
                <SelectItem value={RoundingRule.NEAREST_TEN_THOUSAND}>Nearest $10,000</SelectItem>
                <SelectItem value={RoundingRule.ROUND_UP_THOUSAND}>Round Up $1,000</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="flex items-center space-x-2">
            <Checkbox
              id="portabilityAllowed"
              checked={formData.portabilityAllowed}
              onCheckedChange={(checked) => updateField("portabilityAllowed", checked)}
            />
            <Label htmlFor="portabilityAllowed" className="cursor-pointer">
              Portability Allowed
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="conversionAllowed"
              checked={formData.conversionAllowed}
              onCheckedChange={(checked) => updateField("conversionAllowed", checked)}
            />
            <Label htmlFor="conversionAllowed" className="cursor-pointer">
              Conversion Allowed
            </Label>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : "Save Configuration"}
        </Button>
      </div>
    </form>
  );
}
